import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  PlayArrow,
  DeleteForever,
  Assessment,
  Warning,
  ClearAll,
  Refresh,
  Build,
  Visibility,
  Download,
  CheckCircle,
  Cancel,
  Upload,
  CloudDownload,
  CloudUpload,
  ShoppingCart
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import usePageTitle from '../hooks/usePageTitle';
import { v2 } from '../services/api';

const AdminScripts = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearOrdersDialogOpen, setClearOrdersDialogOpen] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  
  // Database-only phantom detection state
  const [phantomDialogOpen, setPhantomDialogOpen] = useState(false);
  const [phantomPreview, setPhantomPreview] = useState(null);
  const [phantomLoading, setPhantomLoading] = useState(false);
  const [routingSyncLoading, setRoutingSyncLoading] = useState(false);
  const [routingSyncResult, setRoutingSyncResult] = useState(null);
  const [stockV2Loading, setStockV2Loading] = useState(false);
  const [stockV2Result, setStockV2Result] = useState(null);
  const [stockV2Exporting, setStockV2Exporting] = useState(false);
  const [lastCostSyncLoading, setLastCostSyncLoading] = useState(false);
  const [lastCostSyncResult, setLastCostSyncResult] = useState(null);
  const [revertLoading, setRevertLoading] = useState(false);
  const [revertResult, setRevertResult] = useState(null);

  // CSV-based phantom detection state
  const [csvPhantomDialogOpen, setCsvPhantomDialogOpen] = useState(false);
  const [csvPhantomPreview, setCsvPhantomPreview] = useState(null);
  const [csvPhantomLoading, setCsvPhantomLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Database export/import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [clearBeforeImport, setClearBeforeImport] = useState(true);

  // Orders export/import state
  const [ordersImportDialogOpen, setOrdersImportDialogOpen] = useState(false);
  const [ordersImportFile, setOrdersImportFile] = useState(null);
  const [ordersImportLoading, setOrdersImportLoading] = useState(false);
  const [clearBeforeOrdersImport, setClearBeforeOrdersImport] = useState(true);
  

  usePageTitle('Scripts');

  useEffect(() => {
    loadPageInfo();
    loadDatabaseStats();
  }, []);

  const loadPageInfo = async () => {
    try {
      const response = await api.get('/api/admin/scripts/');
      setPageInfo(response.data);
    } catch (error) {
      console.error('Failed to load scripts page:', error);
      toast.error('Failed to load scripts page');
    }
  };

  const loadDatabaseStats = async () => {
    try {
      const response = await api.get('/api/admin/scripts/stats/');
      setDbStats(response.data.database_stats);
    } catch (error) {
      console.error('Failed to load database stats:', error);
      toast.error('Failed to load database statistics');
    }
  };

  const executeScript = async (action) => {
    setLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', { action });
      
      if (action === 'insert_test_data') {
        toast.success('Test data created successfully!');
        setLastAction({ 
          type: 'insert', 
          timestamp: response.data.timestamp,
          details: response.data 
        });
        await loadDatabaseStats();
      } else if (action === 'assign_bom_operations') {
        const results = response.data.results;
        const total = results.adhesive_updated + results.transfer_tape_updated + results.edge_material_updated + results.polyedge_updated;
        toast.success(`Updated ${total} items with operations!`);
        setLastAction({ 
          type: 'assign_bom_ops', 
          timestamp: response.data.timestamp,
          details: response.data 
        });
      } else {
        toast.success('Script executed successfully!');
      }
      
      console.log('Script result:', response.data);
    } catch (error) {
      console.error('Script execution failed:', error);
      toast.error('Script execution failed: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // ==================== DATABASE EXPORT/IMPORT ====================

  const handleExportDatabase = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', 
        { action: 'export_database_dump' },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `db_dump_${new Date().toISOString().slice(0,19).replace(/[:-]/g, '')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Database exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export database: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleImportFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Please select a JSON file');
        return;
      }
      setImportFile(file);
    }
  };

  const handleImportDatabase = async () => {
    if (!importFile) {
      toast.error('Please select a file first');
      return;
    }
    
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('action', 'import_database_dump');
      formData.append('clear_first', clearBeforeImport);
      
      const response = await api.post('/api/admin/scripts/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      setLastAction({ 
        type: 'import_database', 
        timestamp: response.data.timestamp,
        details: response.data 
      });
      setImportDialogOpen(false);
      setImportFile(null);
      // Reset file input
      const fileInput = document.getElementById('import-db-file-input');
      if (fileInput) fileInput.value = '';
      await loadDatabaseStats();
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import database: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setImportLoading(false);
    }
  };

  // ==================== ORDERS EXPORT/IMPORT ====================

  const handleExportOrders = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', 
        { action: 'export_orders_dump' },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_dump_${new Date().toISOString().slice(0,19).replace(/[:-]/g, '')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Orders exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export orders: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleOrdersImportFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Please select a JSON file');
        return;
      }
      setOrdersImportFile(file);
    }
  };

  const handleImportOrders = async () => {
    if (!ordersImportFile) {
      toast.error('Please select a file first');
      return;
    }
    
    setOrdersImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', ordersImportFile);
      formData.append('action', 'import_orders_dump');
      formData.append('clear_first', clearBeforeOrdersImport);
      
      const response = await api.post('/api/admin/scripts/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      setLastAction({ 
        type: 'import_orders', 
        timestamp: response.data.timestamp,
        details: response.data 
      });
      setOrdersImportDialogOpen(false);
      setOrdersImportFile(null);
      // Reset file input
      const fileInput = document.getElementById('import-orders-file-input');
      if (fileInput) fileInput.value = '';
      await loadDatabaseStats();
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import orders: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setOrdersImportLoading(false);
    }
  };

  // ==================== ROUTING OPERATION SYNC ====================

  const handleRoutingSync = async (apply) => {
    if (apply && !window.confirm(
      'Apply routing operation sync? Single-step items whose routing operation '
      + 'diverged from the operation edited in the BOM dialog will be updated.'
    )) return;
    setRoutingSyncLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', {
        action: apply ? 'sync_routing_operations_apply' : 'sync_routing_operations_preview'
      });
      setRoutingSyncResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Routing sync failed:', error);
      toast.error('Routing sync failed: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setRoutingSyncLoading(false);
    }
  };

  // ==================== REBUILD V2 STOCK LEVELS ====================

  const handleStockV2Rebuild = async (apply) => {
    if (apply && !window.confirm(
      'Rebuild v2 stock levels from the ledger?\n\n'
      + 'The cached reserved / issued / produced / scrapped figures are '
      + 'replaced with what the material movement ledger says. Run Preview '
      + 'first to see which items change.'
    )) return;
    setStockV2Loading(true);
    try {
      const response = await api.post('/api/admin/scripts/', {
        action: apply ? 'recalc_stock_v2_apply' : 'recalc_stock_v2_preview',
      });
      setStockV2Result(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Stock v2 rebuild failed:', error);
      toast.error('Rebuild failed: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setStockV2Loading(false);
    }
  };

  const handleStockV2Export = async () => {
    setStockV2Exporting(true);
    try {
      const response = await api.post(
        '/api/admin/scripts/',
        { action: 'recalc_stock_v2_export' },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `v2_stock_rebuild_preview_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rebuild preview exported');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export the rebuild preview');
    } finally {
      setStockV2Exporting(false);
    }
  };

  // ==================== V2 → V1 REVERT ====================

  const handleRevertV2ToV1 = async (execute) => {
    if (execute && !window.confirm(
      'Execute the V2 → V1 revert?\n\n'
      + 'Every order with active V2 work orders will have them retired '
      + '(completed production and allocations are KEPT), and the outstanding '
      + 'quantity on each line will be re-planned as fresh V1 work orders.\n\n'
      + 'Run Preview first and review the numbers — especially the issued-'
      + 'material warnings.'
    )) return;
    setRevertLoading(true);
    try {
      const response = await v2.admin.revertToV1(execute ? { dry_run: false } : {});
      setRevertResult(response.data);
      const d = response.data;
      const retired = d.v2_wos_retired?.length ?? 0;
      const created = d.v1_creation?.created ?? 0;
      const consolidated = d.v1_creation?.consolidated ?? 0;
      if (execute) {
        toast.success(`Reverted — retired ${retired} V2 WOs, created ${created} / updated ${consolidated} V1 WOs`);
      } else {
        toast.info(`Preview — would retire ${retired} V2 WOs and create ${created} / update ${consolidated} V1 WOs`);
      }
    } catch (error) {
      console.error('Revert to V1 failed:', error);
      toast.error(
        error.response?.status === 409
          ? (error.response?.data?.error || 'Revert refused — V1 work-order creation is disabled.')
          : ('Revert failed: ' + (error.response?.data?.error || 'Unknown error'))
      );
    } finally {
      setRevertLoading(false);
    }
  };

  const handleLastCostSync = async (apply) => {
    setLastCostSyncLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', {
        action: apply ? 'sync_last_cost_apply' : 'sync_last_cost_preview'
      });
      setLastCostSyncResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Last cost sync failed:', error);
      toast.error('Last cost sync failed: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setLastCostSyncLoading(false);
    }
  };

  // ==================== DATABASE-ONLY PHANTOM DETECTION ====================

  const handlePhantomPreview = async () => {
    setPhantomLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', { 
        action: 'phantom_detection_preview' 
      });
      setPhantomPreview(response.data);
      setPhantomDialogOpen(true);
    } catch (error) {
      console.error('Phantom detection preview failed:', error);
      toast.error('Failed to preview phantom changes: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setPhantomLoading(false);
    }
  };

  const handlePhantomDownload = async () => {
    try {
      const response = await api.post('/api/admin/scripts/', 
        { action: 'phantom_detection_download' },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `phantom_detection_report_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download report');
    }
  };

  const handlePhantomApply = async () => {
    setPhantomLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', { 
        action: 'phantom_detection_apply' 
      });
      
      toast.success(response.data.message);
      setLastAction({ 
        type: 'phantom_detection', 
        timestamp: response.data.timestamp,
        details: response.data 
      });
      setPhantomDialogOpen(false);
      setPhantomPreview(null);
      await loadDatabaseStats();
    } catch (error) {
      console.error('Phantom detection apply failed:', error);
      toast.error('Failed to apply phantom changes: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setPhantomLoading(false);
    }
  };

  // ==================== CSV-BASED PHANTOM DETECTION ====================

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setCsvPhantomPreview(null);
    }
  };

  const handleCsvPhantomPreview = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file first');
      return;
    }
    
    setCsvPhantomLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('action', 'phantom_detection_from_csv_preview');
      
      const response = await api.post('/api/admin/scripts/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCsvPhantomPreview(response.data);
      setCsvPhantomDialogOpen(true);
    } catch (error) {
      console.error('CSV phantom detection preview failed:', error);
      toast.error('Failed to preview: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setCsvPhantomLoading(false);
    }
  };

  const handleCsvPhantomDownload = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file first');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('action', 'phantom_detection_from_csv_download');
      
      const response = await api.post('/api/admin/scripts/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `phantom_detection_csv_report_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download report');
    }
  };

  const handleCsvPhantomApply = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file first');
      return;
    }
    
    setCsvPhantomLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('action', 'phantom_detection_from_csv_apply');
      
      const response = await api.post('/api/admin/scripts/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      setLastAction({ 
        type: 'csv_phantom_detection', 
        timestamp: response.data.timestamp,
        details: response.data 
      });
      setCsvPhantomDialogOpen(false);
      setCsvPhantomPreview(null);
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('csv-phantom-file-input');
      if (fileInput) fileInput.value = '';
      await loadDatabaseStats();
    } catch (error) {
      console.error('CSV phantom detection apply failed:', error);
      toast.error('Failed to apply: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setCsvPhantomLoading(false);
    }
  };

  // ==================== CLEAR DATA FUNCTIONS ====================

  const handleClearAllData = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', { 
        action: 'clear_all_data',
        confirm: true 
      });

      toast.success('All manufacturing data cleared successfully!');
      setLastAction({ 
        type: 'clear_all', 
        timestamp: response.data.timestamp,
        details: response.data 
      });
      setClearDialogOpen(false);
      await loadDatabaseStats();
    } catch (error) {
      console.error('Failed to clear data:', error);
      toast.error('Failed to clear data: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClearOrdersOnly = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/admin/scripts/', { 
        action: 'clear_orders_only',
        confirm: true 
      });

      toast.success('Customer orders and work orders cleared successfully!');
      setLastAction({ 
        type: 'clear_orders', 
        timestamp: response.data.timestamp,
        details: response.data 
      });
      setClearOrdersDialogOpen(false);
      await loadDatabaseStats();
    } catch (error) {
      console.error('Failed to clear orders:', error);
      toast.error('Failed to clear orders: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Admin Scripts
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Development and testing utilities for database management
      </Typography>

      {pageInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Page loaded at {new Date(pageInfo.timestamp).toLocaleString()} for user: {pageInfo.user}
        </Alert>
      )}

      {lastAction && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            Last Action: {
              lastAction.type === 'clear_all' ? 'All Data Cleared' : 
              lastAction.type === 'clear_orders' ? 'Orders Cleared' : 
              lastAction.type === 'assign_bom_ops' ? 'BOM Operations Assigned' :
              lastAction.type === 'phantom_detection' ? 'Phantom Detection Applied' :
              lastAction.type === 'csv_phantom_detection' ? 'CSV Phantom Detection Applied' :
              lastAction.type === 'import_database' ? 'Database Imported' :
              lastAction.type === 'import_orders' ? 'Orders Imported' :
              'Script Executed'
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(lastAction.timestamp).toLocaleString()}
          </Typography>
          {lastAction.details?.deleted_counts && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Records deleted:</Typography>
              {Object.entries(lastAction.details.deleted_counts).map(([key, value]) => (
                <Chip 
                  key={key} 
                  label={`${key.replace(/_/g, ' ')}: ${value}`} 
                  size="small" 
                  sx={{ mr: 1, mb: 0.5 }} 
                />
              ))}
            </Box>
          )}
          {lastAction.details?.results && lastAction.type === 'assign_bom_ops' && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Items updated:</Typography>
              <Chip label={`Adhesive: ${lastAction.details.results.adhesive_updated}`} size="small" sx={{ mr: 1, mb: 0.5 }} />
              <Chip label={`Transfer Tape: ${lastAction.details.results.transfer_tape_updated}`} size="small" sx={{ mr: 1, mb: 0.5 }} />
              <Chip label={`Edge Material: ${lastAction.details.results.edge_material_updated}`} size="small" sx={{ mr: 1, mb: 0.5 }} />
              <Chip label={`Polyedge: ${lastAction.details.results.polyedge_updated}`} size="small" sx={{ mr: 1, mb: 0.5 }} />
              {lastAction.details.results.operations_created?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">Operations created:</Typography>
                  {lastAction.details.results.operations_created.map((op) => (
                    <Chip key={op} label={op} size="small" color="success" sx={{ mr: 1, mb: 0.5 }} />
                  ))}
                </Box>
              )}
            </Box>
          )}
          {lastAction.details?.results && lastAction.type === 'phantom_detection' && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Phantom changes:</Typography>
              <Chip label={`Marked phantom: ${lastAction.details.results.items_marked_phantom}`} size="small" color="warning" sx={{ mr: 1, mb: 0.5 }} />
              <Chip label={`Unmarked phantom: ${lastAction.details.results.items_unmarked_phantom}`} size="small" color="info" sx={{ mr: 1, mb: 0.5 }} />
              <Chip label={`Total updated: ${lastAction.details.results.total_updated}`} size="small" color="success" sx={{ mr: 1, mb: 0.5 }} />
            </Box>
          )}
          {lastAction.details?.results && lastAction.type === 'csv_phantom_detection' && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">CSV Phantom changes:</Typography>
              <Chip label={`Operations assigned: ${lastAction.details.results.operations_assigned}`} size="small" color="success" sx={{ mr: 1, mb: 0.5 }} />
              <Chip label={`Marked phantom: ${lastAction.details.results.phantoms_marked}`} size="small" color="warning" sx={{ mr: 1, mb: 0.5 }} />
              <Chip label={`Unmarked phantom: ${lastAction.details.results.phantoms_unmarked}`} size="small" color="info" sx={{ mr: 1, mb: 0.5 }} />
              <Chip label={`Total updated: ${lastAction.details.results.total_updated}`} size="small" color="primary" sx={{ mr: 1, mb: 0.5 }} />
            </Box>
          )}
          {lastAction.details?.model_counts && (lastAction.type === 'import_database' || lastAction.type === 'import_orders') && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Objects imported by model:</Typography>
              {Object.entries(lastAction.details.model_counts).slice(0, 10).map(([model, count]) => (
                <Chip 
                  key={model} 
                  label={`${model.split('.').pop()}: ${count}`} 
                  size="small" 
                  color="primary"
                  sx={{ mr: 1, mb: 0.5 }} 
                />
              ))}
              {Object.keys(lastAction.details.model_counts).length > 10 && (
                <Chip label={`+${Object.keys(lastAction.details.model_counts).length - 10} more`} size="small" sx={{ mr: 1, mb: 0.5 }} />
              )}
            </Box>
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Database Statistics */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Database Statistics</Typography>
              </Box>
              
              {dbStats ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Table</strong></TableCell>
                        <TableCell align="right"><strong>Records</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(dbStats).map(([table, count]) => (
                        <TableRow key={table}>
                          <TableCell>{table.replace(/_/g, ' ').toUpperCase()}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={count} 
                              size="small" 
                              color={count > 0 ? 'primary' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <CircularProgress size={24} />
              )}
            </CardContent>
            <CardActions>
              <Button 
                startIcon={<Refresh />}
                onClick={loadDatabaseStats}
                disabled={loading}
              >
                Refresh Stats
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Database Export/Import */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'primary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudDownload sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary.main">Database Export / Import</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export the full database to JSON or import a database dump from another environment (e.g. production to local).
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Uses Django's dumpdata/loaddata for reliable data transfer with proper foreign key handling.
                </Typography>
              </Alert>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>Import from file:</Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportFileSelect}
                  style={{ display: 'none' }}
                  id="import-db-file-input"
                />
                <label htmlFor="import-db-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    color="primary"
                    startIcon={<Upload />}
                    size="small"
                  >
                    Select JSON File
                  </Button>
                </label>
                {importFile && (
                  <Chip 
                    label={importFile.name} 
                    onDelete={() => {
                      setImportFile(null);
                      const fileInput = document.getElementById('import-db-file-input');
                      if (fileInput) fileInput.value = '';
                    }}
                    sx={{ ml: 1 }}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Button 
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <CloudDownload />}
                onClick={handleExportDatabase}
                disabled={loading}
              >
                {loading ? 'Exporting...' : 'Export Database'}
              </Button>
              <Button 
                variant="outlined"
                color="primary"
                startIcon={<CloudUpload />}
                onClick={() => setImportDialogOpen(true)}
                disabled={loading || !importFile}
              >
                Import Database
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Orders Export/Import */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingCart sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6" color="warning.main">Orders Export / Import</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export only customer orders and work orders to JSON, or import them from another environment.
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Includes:</strong> Customer Orders, Order Lines, Work Orders, Executions, Stock Allocations, DX Consignments
                </Typography>
              </Alert>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>Import from file:</Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleOrdersImportFileSelect}
                  style={{ display: 'none' }}
                  id="import-orders-file-input"
                />
                <label htmlFor="import-orders-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    color="warning"
                    startIcon={<Upload />}
                    size="small"
                  >
                    Select JSON File
                  </Button>
                </label>
                {ordersImportFile && (
                  <Chip 
                    label={ordersImportFile.name} 
                    onDelete={() => {
                      setOrdersImportFile(null);
                      const fileInput = document.getElementById('import-orders-file-input');
                      if (fileInput) fileInput.value = '';
                    }}
                    sx={{ ml: 1 }}
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Button 
                variant="contained"
                color="warning"
                startIcon={loading ? <CircularProgress size={20} /> : <CloudDownload />}
                onClick={handleExportOrders}
                disabled={loading}
              >
                {loading ? 'Exporting...' : 'Export Orders'}
              </Button>
              <Button 
                variant="outlined"
                color="warning"
                startIcon={<CloudUpload />}
                onClick={() => setOrdersImportDialogOpen(true)}
                disabled={loading || !ordersImportFile}
              >
                Import Orders
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* CSV-Based Phantom Detection */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'secondary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Visibility sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="h6" color="secondary.main">Phantom Detection (from CSV)</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload the original BOM CSV file to accurately detect phantoms based on AG_ID matching.
                This also assigns missing assembly_operations.
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>More accurate than database-only detection</strong> - uses AG_ID from CSV headers 
                  to determine phantom status, even for items without assembly_operation assigned.
                </Typography>
              </Alert>
              
              <Box sx={{ mb: 2 }}>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="csv-phantom-file-input"
                />
                <label htmlFor="csv-phantom-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    color="secondary"
                    startIcon={<Upload />}
                  >
                    Select CSV File
                  </Button>
                </label>
                {selectedFile && (
                  <Chip 
                    label={selectedFile.name} 
                    onDelete={() => {
                      setSelectedFile(null);
                      setCsvPhantomPreview(null);
                      const fileInput = document.getElementById('csv-phantom-file-input');
                      if (fileInput) fileInput.value = '';
                    }}
                    sx={{ ml: 1 }}
                    color="secondary"
                  />
                )}
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>This will:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip 
                  label="Detect phantoms from AG_ID matching" 
                  size="small" 
                  variant="outlined"
                  color="secondary"
                />
                <Chip 
                  label="Assign missing assembly_operations" 
                  size="small" 
                  variant="outlined"
                  color="secondary"
                />
                <Chip 
                  label="Fix incorrect phantom status" 
                  size="small" 
                  variant="outlined"
                  color="secondary"
                />
              </Box>
            </CardContent>
            <CardActions>
              <Button 
                variant="contained"
                color="secondary"
                startIcon={csvPhantomLoading ? <CircularProgress size={20} /> : <Visibility />}
                onClick={handleCsvPhantomPreview}
                disabled={csvPhantomLoading || loading || !selectedFile}
              >
                {csvPhantomLoading ? 'Scanning...' : 'Preview Changes'}
              </Button>
              <Button 
                variant="outlined"
                color="secondary"
                startIcon={<Download />}
                onClick={handleCsvPhantomDownload}
                disabled={csvPhantomLoading || loading || !selectedFile}
              >
                Download Report
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Database-Only Phantom Detection */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'grey.400' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Visibility sx={{ mr: 1, color: 'grey.600' }} />
                <Typography variant="h6" color="text.secondary">Phantom Detection (Database Only)</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Scan existing database items to detect phantoms. Only works for items that already have assembly_operation assigned.
              </Typography>
              
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Limited:</strong> Cannot detect phantoms for items missing assembly_operation. 
                  Use the CSV-based detection above for more accurate results.
                </Typography>
              </Alert>
              
              <Typography variant="subtitle2" gutterBottom>Detection rule:</Typography>
              <Chip 
                label="Item is phantom if its operation matches parent's step operation" 
                size="small" 
                variant="outlined"
                color="default"
              />
            </CardContent>
            <CardActions>
              <Button 
                variant="outlined"
                color="inherit"
                startIcon={phantomLoading ? <CircularProgress size={20} /> : <Visibility />}
                onClick={handlePhantomPreview}
                disabled={phantomLoading || loading}
              >
                {phantomLoading ? 'Scanning...' : 'Preview Changes'}
              </Button>
              <Button 
                variant="text"
                color="inherit"
                startIcon={<Download />}
                onClick={handlePhantomDownload}
                disabled={phantomLoading || loading}
              >
                Download Report
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Assign BOM Operations */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'info.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Build sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6" color="info.main">Assign BOM Operations</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Automatically assign operations to items based on their names.
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom>Rules applied:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Chip 
                  label='"Adhesive useage Square Metre" -> OP-21' 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
                <Chip 
                  label='"Transfer Tape (Enviro)" -> OP-13' 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
                <Chip 
                  label='"Edge Material per Metre" -> OP-13' 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
                <Chip 
                  label='"Polyedge Metre Run" -> OP-18' 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
              </Box>
              
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Updates Item.assembly_operation based on item name matching
                </Typography>
              </Alert>
            </CardContent>
            <CardActions>
              <Button 
                variant="contained"
                color="info"
                startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => executeScript('assign_bom_operations')}
                disabled={loading}
              >
                {loading ? 'Assigning...' : 'Assign BOM Operations'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Sync Routing Operations */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'info.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Build sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6" color="info.main">Sync Routing Operations</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Repairs items whose routing operation diverged from the operation edited in
                the BOM dialog (edits used to save to the item only, so work orders kept
                using the old operation). Single-step items are fixed to the edited
                operation; multi-step items are listed for review in the routing editor.
              </Typography>

              {routingSyncResult && (
                <Alert
                  severity={routingSyncResult.preview ? 'info' : 'success'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" sx={{ mb: routingSyncResult.fixable?.length ? 1 : 0 }}>
                    {routingSyncResult.preview
                      ? `Preview: ${routingSyncResult.summary.fixable} item(s) would be fixed`
                      : `Applied: ${routingSyncResult.summary.updated} routing step(s) updated`}
                    {routingSyncResult.summary.ambiguous_multi_step > 0 &&
                      ` — ${routingSyncResult.summary.ambiguous_multi_step} multi-step item(s) need manual review`}
                  </Typography>
                  {(routingSyncResult.fixable || []).slice(0, 15).map(row => (
                    <Typography key={row.item_code} variant="caption" display="block">
                      {row.item_code}: {row.routing_operation} → {row.new_operation}
                    </Typography>
                  ))}
                  {(routingSyncResult.fixable || []).length > 15 && (
                    <Typography variant="caption" display="block">
                      …and {routingSyncResult.fixable.length - 15} more
                    </Typography>
                  )}
                  {(routingSyncResult.ambiguous || []).map(row => (
                    <Typography key={row.item_code} variant="caption" display="block" color="warning.main">
                      MULTI-STEP {row.item_code}: item says {row.assembly_operation}, routing {row.routing}
                    </Typography>
                  ))}
                </Alert>
              )}
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="info"
                startIcon={routingSyncLoading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => handleRoutingSync(false)}
                disabled={routingSyncLoading || loading}
              >
                {routingSyncLoading ? 'Working...' : 'Preview'}
              </Button>
              <Button
                variant="contained"
                color="info"
                startIcon={routingSyncLoading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => handleRoutingSync(true)}
                disabled={routingSyncLoading || loading}
              >
                {routingSyncLoading ? 'Working...' : 'Apply Fix'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Rebuild V2 Stock Levels — full width: the preview table is wide */}
        <Grid item xs={12}>
          <Card sx={{ border: '2px solid', borderColor: 'info.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Build sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6" color="info.main">Rebuild V2 Stock Levels</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Recalculates the cached v2 stock figures from the material movement
                ledger (<code>manage.py recalculate_stock_level_v2</code>). The cached
                reserved figure used to drift low — every issued unit was taken off
                twice — so stock looked free that orders had already claimed.
                Only <strong>reserved</strong> changes what is free: reserved +N means
                N fewer units look free. Work-order netting and Material Shortages
                read the ledger directly, so they are not affected.
              </Typography>

              {stockV2Result && (
                <Alert severity={stockV2Result.preview ? 'info' : 'success'} sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {stockV2Result.preview
                      ? `Preview: ${stockV2Result.summary.items_changing} of ${stockV2Result.summary.items_checked} items would change`
                      : `Applied: rebuilt ${stockV2Result.summary.rebuilt} items — ${stockV2Result.summary.items_changing} changed`
                        + (stockV2Result.summary.still_differing
                          ? `, ${stockV2Result.summary.still_differing} still differ (stock moved during the run — preview again)`
                          : '')}
                    {' '}(reserved up on {stockV2Result.summary.reserved_up},
                    down on {stockV2Result.summary.reserved_down}
                    {stockV2Result.summary.history_only > 0
                      ? `, history totals only on ${stockV2Result.summary.history_only}`
                      : ''})
                    {stockV2Result.summary.free_goes_negative > 0 && (
                      <strong>
                        {` — free stock goes negative on ${stockV2Result.summary.free_goes_negative} item(s)`}
                      </strong>
                    )}
                  </Typography>
                  {(stockV2Result.items || []).length > 0 && (
                    <TableContainer sx={{ maxHeight: 420, overflowX: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">On hand</TableCell>
                            <TableCell align="right">Allocated to orders</TableCell>
                            <TableCell align="right">v1 reserved</TableCell>
                            <TableCell align="right">v2 reserved now → after</TableCell>
                            <TableCell align="right">Free now → after</TableCell>
                            <TableCell align="right">Free after allocations</TableCell>
                            <TableCell align="right">Orders reserving</TableCell>
                            <TableCell>History (issued / produced / scrapped)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockV2Result.items.map((row) => {
                            const change = parseFloat(row.reserved_change);
                            const freeAfter = parseFloat(row.free_after);
                            const freeAlloc = parseFloat(row.free_after_allocations);
                            const hist = [
                              ['issued', row.issued_before, row.issued_after],
                              ['produced', row.produced_before, row.produced_after],
                              ['scrapped', row.scrapped_before, row.scrapped_after],
                            ];
                            return (
                              <TableRow key={row.item_id}>
                                <TableCell sx={{ minWidth: 200 }}>
                                  <Typography variant="caption" fontWeight="bold" display="block">{row.item_code}</Typography>
                                  <Typography variant="caption" color="text.secondary">{row.item_name}</Typography>
                                </TableCell>
                                <TableCell align="right">{row.on_hand}</TableCell>
                                <TableCell align="right">{row.allocated_to_orders}</TableCell>
                                <TableCell align="right">{row.v1_reserved}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                  {row.reserved_before} → <strong>{row.reserved_after}</strong>
                                  {change !== 0 && (
                                    <Typography variant="caption" display="block" color={change > 0 ? 'warning.main' : 'success.main'}>
                                      {change > 0 ? '+' : ''}{row.reserved_change}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                  {row.free_before} →{' '}
                                  <Typography component="span" variant="body2" fontWeight="bold" color={freeAfter < 0 ? 'error.main' : 'inherit'}>
                                    {row.free_after}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography component="span" variant="body2" fontWeight="bold" color={freeAlloc < 0 ? 'error.main' : 'inherit'}>
                                    {row.free_after_allocations}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{row.orders_reserving}</TableCell>
                                <TableCell>
                                  <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                    {hist.map(([label, before, after]) => (
                                      before === after ? `${label} ${after}` : `${label} ${before} → ${after}`
                                    )).join(' · ')}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {stockV2Result.items_truncated > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      …and {stockV2Result.items_truncated} more (largest changes shown first) — Export to Excel for the full list
                    </Typography>
                  )}
                </Alert>
              )}
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="info"
                startIcon={stockV2Loading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => handleStockV2Rebuild(false)}
                disabled={stockV2Loading || loading}
              >
                {stockV2Loading ? 'Working...' : 'Preview'}
              </Button>
              <Button
                variant="contained"
                color="info"
                startIcon={stockV2Loading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => handleStockV2Rebuild(true)}
                disabled={stockV2Loading || loading}
              >
                {stockV2Loading ? 'Working...' : 'Apply Rebuild'}
              </Button>
              <Button
                variant="outlined"
                color="info"
                startIcon={stockV2Exporting ? <CircularProgress size={20} /> : <Download />}
                onClick={handleStockV2Export}
                disabled={stockV2Exporting || stockV2Loading || loading}
              >
                {stockV2Exporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* V2 → V1 Revert */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'error.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Refresh sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" color="error.main">Revert V2 → V1 (swap back)</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Swap production back to the V1 work-order system. Stock levels,
                allocations and order statuses are left exactly as they are; every
                order&apos;s active V2 work orders are retired (completed production is
                kept), and the outstanding quantity on each line is re-planned as
                fresh V1 work orders — sized <em>ordered − shipped − allocated</em>,
                netted against free stock, earliest due date first.
              </Typography>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Requires V1_WO_CREATION_ENABLED=true.</strong> Material already
                  issued by in-progress V2 steps stays consumed — the preview lists those
                  work orders so stock can be adjusted manually where appropriate.
                </Typography>
              </Alert>

              {revertResult && (
                <Alert severity={revertResult.dry_run ? 'info' : 'success'} sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {revertResult.dry_run ? 'Preview' : 'Executed'} — {' '}
                    {revertResult.v2_wos_retired?.length ?? 0} V2 WO(s) retired, {' '}
                    {revertResult.v1_creation?.created ?? 0} V1 WO(s) created, {' '}
                    {revertResult.v1_creation?.consolidated ?? 0} updated, {' '}
                    {revertResult.lines_planned?.length ?? 0} line(s) planned, {' '}
                    {revertResult.v2_wos_completed_kept ?? 0} completed V2 WO(s) kept
                  </Typography>
                  {(revertResult.orders_skipped || []).slice(0, 8).map((o) => (
                    <Typography key={o.order_id} variant="caption" display="block">
                      Skipped {o.order_number}: {o.reason === 'already_has_active_v1_wos'
                        ? 'already has active V1 work orders'
                        : `status ${o.status}`}
                    </Typography>
                  ))}
                  {(revertResult.wip_warnings || []).map((w) => (
                    <Typography key={w.wo_number} variant="caption" display="block" color="warning.main">
                      ⚠ {w.wo_number} ({w.output_item_code}) had issued material: {' '}
                      {w.issued_material.map((m) => `${m.quantity} × ${m.item_code}`).join(', ')}
                      {' '}— stays consumed
                    </Typography>
                  ))}
                </Alert>
              )}
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="error"
                startIcon={revertLoading ? <CircularProgress size={20} /> : <Visibility />}
                onClick={() => handleRevertV2ToV1(false)}
                disabled={revertLoading || loading}
              >
                {revertLoading ? 'Working...' : 'Preview'}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={revertLoading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => handleRevertV2ToV1(true)}
                disabled={revertLoading || loading || !revertResult?.dry_run}
              >
                {revertLoading ? 'Working...' : 'Execute Revert'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Promote Standard Cost to Last Cost */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'info.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Build sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6" color="info.main">Promote Standard Cost → Last Cost</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The Inventory Value report values stock at the Sage last cost first.
                Items never purchased come in from Sage with a £0.00 last cost, so
                costs entered in the Item screen (standard cost) don&apos;t reach the
                valuation. This copies the standard cost into last cost for every
                item whose last cost is blank or £0.00 and whose standard cost is set.
              </Typography>

              {lastCostSyncResult && (
                <Alert
                  severity={lastCostSyncResult.preview ? 'info' : 'success'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" sx={{ mb: lastCostSyncResult.rows?.length ? 1 : 0 }}>
                    {lastCostSyncResult.preview
                      ? `Preview: ${lastCostSyncResult.summary.fixable} item(s) would be updated`
                      : `Applied: ${lastCostSyncResult.summary.updated} item(s) updated`}
                  </Typography>
                  {(lastCostSyncResult.rows || []).slice(0, 15).map(row => (
                    <Typography key={row.item_code} variant="caption" display="block">
                      {row.item_code}: {row.last_cost_price === null ? 'blank' : `£${row.last_cost_price}`} → £{row.standard_cost}
                    </Typography>
                  ))}
                  {lastCostSyncResult.summary.fixable > 15 && (
                    <Typography variant="caption" display="block">
                      …and {lastCostSyncResult.summary.fixable - 15} more
                    </Typography>
                  )}
                </Alert>
              )}
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="info"
                startIcon={lastCostSyncLoading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => handleLastCostSync(false)}
                disabled={lastCostSyncLoading || loading}
              >
                {lastCostSyncLoading ? 'Working...' : 'Preview'}
              </Button>
              <Button
                variant="contained"
                color="info"
                startIcon={lastCostSyncLoading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => handleLastCostSync(true)}
                disabled={lastCostSyncLoading || loading}
              >
                {lastCostSyncLoading ? 'Working...' : 'Apply'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Clear Orders Only */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ClearAll sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6" color="warning.main">Clear Orders Only</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Delete only customer orders and work orders. Items, BOMs, operations, and stock levels will be preserved.
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Perfect for testing:</strong> Clear orders and re-run the same import to test changes.
                </Typography>
              </Alert>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {[
                  'Customer Orders',
                  'Work Orders',
                  'Stock Allocations',
                  'DX Consignments'
                ].map((item, index) => (
                  <Chip 
                    key={index}
                    label={item} 
                    size="small" 
                    color="warning"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
            <CardActions>
              <Button 
                variant="contained"
                color="warning"
                startIcon={<ClearAll />}
                onClick={() => setClearOrdersDialogOpen(true)}
                disabled={loading}
              >
                Clear Orders
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Insert Test Data */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PlayArrow sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6" color="success.main">Insert Test Data</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create comprehensive test data matching your current system structure with realistic stock quantities.
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom>Test data includes:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {[
                  'Raw materials with stock',
                  'Manufacturing operations',
                  'Component items',
                  'Complete BOM structure'
                ].map((item, index) => (
                  <Chip 
                    key={index}
                    label={item} 
                    size="small" 
                    variant="outlined"
                    color="success"
                  />
                ))}
              </Box>
              
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Creates the exact items, operations, and BOM structure shown in your current system
                </Typography>
              </Alert>
            </CardContent>
            <CardActions>
              <Button 
                variant="contained"
                color="success"
                startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={() => executeScript('insert_test_data')}
                disabled={loading}
              >
                {loading ? 'Creating Data...' : 'Insert Test Data'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Clear All Data */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '2px solid', borderColor: 'error.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DeleteForever sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" color="error">Clear All Manufacturing Data</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permanently delete all manufacturing data from the database. Users and user profiles will be preserved.
              </Typography>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Warning:</strong> This will delete all items, work orders, BOMs, stock levels, 
                  customer orders, and manufacturing data. This action cannot be undone.
                </Typography>
              </Alert>
            </CardContent>
            <CardActions>
              <Button 
                variant="contained"
                color="error"
                startIcon={<DeleteForever />}
                onClick={() => setClearDialogOpen(true)}
                disabled={loading}
              >
                Clear All Data
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Import Database Confirmation Dialog */}
      <Dialog 
        open={importDialogOpen} 
        onClose={() => setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <CloudUpload sx={{ mr: 1, color: 'primary.main' }} />
          Import Database Dump
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to import: <strong>{importFile?.name}</strong>
          </DialogContentText>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={clearBeforeImport}
                onChange={(e) => setClearBeforeImport(e.target.checked)}
                color="warning"
              />
            }
            label="Clear existing data before import"
            sx={{ mt: 2 }}
          />
          
          {clearBeforeImport ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> All existing manufacturing data will be deleted before importing. 
                User accounts will be preserved.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Import will attempt to add/update records. May fail if there are conflicts with existing data.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setImportDialogOpen(false)}
            disabled={importLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImportDatabase}
            color="primary"
            variant="contained"
            disabled={importLoading}
            startIcon={importLoading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {importLoading ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Orders Confirmation Dialog */}
      <Dialog 
        open={ordersImportDialogOpen} 
        onClose={() => setOrdersImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <ShoppingCart sx={{ mr: 1, color: 'warning.main' }} />
          Import Orders Dump
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to import: <strong>{ordersImportFile?.name}</strong>
          </DialogContentText>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={clearBeforeOrdersImport}
                onChange={(e) => setClearBeforeOrdersImport(e.target.checked)}
                color="warning"
              />
            }
            label="Clear existing orders before import"
            sx={{ mt: 2 }}
          />
          
          {clearBeforeOrdersImport ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> All existing customer orders and work orders will be deleted before importing.
                Items, BOMs, and stock levels will be preserved.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Import will attempt to add/update records. May fail if there are conflicts with existing data.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOrdersImportDialogOpen(false)}
            disabled={ordersImportLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImportOrders}
            color="warning"
            variant="contained"
            disabled={ordersImportLoading}
            startIcon={ordersImportLoading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {ordersImportLoading ? 'Importing...' : 'Import Orders'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Phantom Detection Preview Dialog */}
      <Dialog 
        open={csvPhantomDialogOpen} 
        onClose={() => setCsvPhantomDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Visibility sx={{ mr: 1, color: 'secondary.main' }} />
          Phantom Detection Preview (from CSV)
        </DialogTitle>
        <DialogContent>
          {csvPhantomPreview && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>CSV Stats:</strong> {csvPhantomPreview.stats.total_rows} rows, {' '}
                  {csvPhantomPreview.stats.header_rows} header rows, {' '}
                  {csvPhantomPreview.stats.items_with_routing} items with routing, {' '}
                  {csvPhantomPreview.stats.items_as_components} items as components
                </Typography>
              </Alert>
              
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Changes:</strong> {csvPhantomPreview.summary.items_to_assign_operation} operations to assign, {' '}
                  {csvPhantomPreview.summary.items_to_mark_phantom} to mark phantom, {' '}
                  {csvPhantomPreview.summary.items_to_unmark_phantom} to unmark phantom, {' '}
                  {csvPhantomPreview.summary.total_changes} total
                </Typography>
              </Alert>
              
              {csvPhantomPreview.changes.length === 0 ? (
                <Alert severity="success">
                  <Typography variant="body2">
                    No changes needed - all items are correctly configured!
                  </Typography>
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Item Code</strong></TableCell>
                        <TableCell><strong>Item Name</strong></TableCell>
                        <TableCell><strong>Action</strong></TableCell>
                        <TableCell><strong>Current Op</strong></TableCell>
                        <TableCell><strong>New Op</strong></TableCell>
                        <TableCell><strong>Reason</strong></TableCell>
                        <TableCell><strong>Parent</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvPhantomPreview.changes.map((change, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                              {change.item_code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                              {change.item_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={
                                change.action === 'mark_phantom' ? 'Mark Phantom' : 
                                change.action === 'unmark_phantom' ? 'Unmark Phantom' :
                                'Assign Op'
                              }
                              size="small"
                              color={
                                change.action === 'mark_phantom' ? 'warning' : 
                                change.action === 'unmark_phantom' ? 'info' :
                                'success'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                              {change.current_operation || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" color="success.main" sx={{ fontSize: '0.75rem' }}>
                              {change.new_operation || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200, fontSize: '0.65rem' }}>
                              {change.reason}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                              {change.parent_item || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCsvPhantomDownload}
            startIcon={<Download />}
            disabled={csvPhantomLoading}
          >
            Download CSV
          </Button>
          <Button 
            onClick={() => setCsvPhantomDialogOpen(false)}
            disabled={csvPhantomLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCsvPhantomApply}
            color="secondary"
            variant="contained"
            disabled={csvPhantomLoading || (csvPhantomPreview && csvPhantomPreview.changes.length === 0)}
            startIcon={csvPhantomLoading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {csvPhantomLoading ? 'Applying...' : 'Apply Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Database-Only Phantom Detection Preview Dialog */}
      <Dialog 
        open={phantomDialogOpen} 
        onClose={() => setPhantomDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Visibility sx={{ mr: 1, color: 'grey.600' }} />
          Phantom Detection Preview (Database Only)
        </DialogTitle>
        <DialogContent>
          {phantomPreview && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Summary:</strong> {phantomPreview.summary.items_to_mark_phantom} items to mark as phantom, {' '}
                  {phantomPreview.summary.items_to_unmark_phantom} items to unmark as phantom, {' '}
                  {phantomPreview.summary.total_changes} total changes
                </Typography>
              </Alert>
              
              {phantomPreview.changes.length === 0 ? (
                <Alert severity="success">
                  <Typography variant="body2">
                    No changes needed - all items are correctly configured!
                  </Typography>
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Item Code</strong></TableCell>
                        <TableCell><strong>Item Name</strong></TableCell>
                        <TableCell><strong>Action</strong></TableCell>
                        <TableCell><strong>Reason</strong></TableCell>
                        <TableCell><strong>Parent Item</strong></TableCell>
                        <TableCell><strong>Step</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {phantomPreview.changes.map((change, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {change.item_code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {change.item_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={change.action === 'mark_phantom' ? 'Mark Phantom' : 'Unmark Phantom'}
                              size="small"
                              color={change.action === 'mark_phantom' ? 'warning' : 'info'}
                              icon={change.action === 'mark_phantom' ? <CheckCircle /> : <Cancel />}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 250, fontSize: '0.75rem' }}>
                              {change.reason}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {change.parent_item || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {change.consumed_in_step || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handlePhantomDownload}
            startIcon={<Download />}
            disabled={phantomLoading}
          >
            Download CSV
          </Button>
          <Button 
            onClick={() => setPhantomDialogOpen(false)}
            disabled={phantomLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePhantomApply}
            color="primary"
            variant="contained"
            disabled={phantomLoading || (phantomPreview && phantomPreview.changes.length === 0)}
            startIcon={phantomLoading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {phantomLoading ? 'Applying...' : 'Apply Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Data Confirmation Dialog */}
      <Dialog 
        open={clearDialogOpen} 
        onClose={() => setClearDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Warning sx={{ mr: 1, color: 'error.main' }} />
          Confirm Full Data Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you absolutely sure you want to delete ALL manufacturing data? 
            This action is permanent and cannot be undone.
          </DialogContentText>
          <Alert severity="error" sx={{ mt: 2 }}>
            This will delete all manufacturing records but preserve user accounts and profiles.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setClearDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleClearAllData}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteForever />}
          >
            {loading ? 'Clearing...' : 'Yes, Delete Everything'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Orders Only Confirmation Dialog */}
      <Dialog 
        open={clearOrdersDialogOpen} 
        onClose={() => setClearOrdersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Warning sx={{ mr: 1, color: 'warning.main' }} />
          Confirm Order Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete all customer orders and work orders but keep your items, BOMs, operations, and stock levels.
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>What gets deleted:</strong>
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>All customer orders and their lines</li>
              <li>All work orders and executions</li>
              <li>Stock allocations</li>
              <li>DX Consignments</li>
            </ul>
          </Alert>
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>What's preserved:</strong>
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>All items and stock levels</li>
              <li>BOMs and operations</li>
              <li>User accounts</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setClearOrdersDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleClearOrdersOnly}
            color="warning"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <ClearAll />}
          >
            {loading ? 'Clearing Orders...' : 'Yes, Clear Orders'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminScripts;